import { Unit } from "@corpuscomplete/core/common/unit";
import { BodyCharacteristic } from "@corpuscomplete/core/body/body-characteristic";
import { z } from "zod/v4";

export namespace Rmr {
  const MAX_WEIGHT = 1500;
  const MAX_HEIGHT = 300;
  const MAX_AGE = 150;

  const weightKeySchema = Unit.keySchema("mass").extract(["kg", "lb"]);
  const heightKeySchema = Unit.keySchema("length").extract(["cm", "in"]);

  const weightSchema = z.object({
    quantity: z.number().positive().max(MAX_WEIGHT),
    unitKey: weightKeySchema,
  });

  const heightSchema = z.object({
    quantity: z.number().positive().max(MAX_HEIGHT),
    unitKey: heightKeySchema,
  });

  const cunnninghamSchema = z.object({
    type: z.literal("cunningham"),
    weight: weightSchema,
    leanBodyMass: weightSchema,
  });

  const harrisBenedictSchema = z.object({
    type: z.literal("harris-benedict"),
    biologicalSex: BodyCharacteristic.biologicalSexSchema,
    age: z.number().positive().max(MAX_AGE),
    weight: weightSchema,
    height: heightSchema,
  });

  const mifflinStJeorSchema = z.object({
    type: z.literal("mifflin-st-jeor"),
    biologicalSex: BodyCharacteristic.biologicalSexSchema,
    age: z.number().positive().max(MAX_AGE),
    weight: weightSchema,
    height: heightSchema,
  });

  export const parametersSchema = z.union([
    cunnninghamSchema,
    harrisBenedictSchema,
    mifflinStJeorSchema,
  ]);
  export type Parameters = z.infer<typeof parametersSchema>;

  export const equationSchema = z.enum(["cunningham", "harris-benedict", "mifflin-st-jeor"]);
  export type Equation = z.infer<typeof equationSchema>;

  export const calculate = (parameters: Parameters): number => {
    switch (parameters.type) {
      case "cunningham":
        return calculateCunningham(Unit.convert(parameters.leanBodyMass, "kg"));
      case "harris-benedict":
        return calculateHarrisBenedict(
          Unit.convert(parameters.weight, "kg"),
          Unit.convert(parameters.height, "cm"),
          parameters.age,
          parameters.biologicalSex,
        );
      case "mifflin-st-jeor":
        return calculateMifflinStJeor(
          Unit.convert(parameters.weight, "kg"),
          Unit.convert(parameters.height, "cm"),
          parameters.age,
          parameters.biologicalSex,
        );
    }
  };

  const calculateCunningham = (leanBodyMass: Unit.Quantity<"mass", "kg">) => {
    return 500 + 22 * leanBodyMass.quantity;
  };

  const calculateHarrisBenedict = (
    weight: Unit.Quantity<"mass", "kg">,
    height: Unit.Quantity<"length", "cm">,
    age: number,
    biologicalSex: BodyCharacteristic.BiologicalSex,
  ) => {
    // TODO: How to handle "other"?
    if (biologicalSex === "female") {
      return 655.1 + 9.563 * weight.quantity + 1.85 * height.quantity - 4.676 * age;
    } else {
      return 66.5 + 13.75 * weight.quantity + 5.003 * height.quantity - 6.75 * age;
    }
  };

  const calculateMifflinStJeor = (
    weight: Unit.Quantity<"mass", "kg">,
    height: Unit.Quantity<"length", "cm">,
    age: number,
    biologicalSex: BodyCharacteristic.BiologicalSex,
  ) => {
    // TODO: How to handle "other"?
    const s = biologicalSex === "female" ? -161 : 5;
    return 10 * weight.quantity + 6.25 * height.quantity - 5 * age + s;
  };
}
